import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";
import { refreshPushSubscription } from "./push";
import { queryKeys } from "./queryKeys";
import { useSocket } from "./socket";
import type { AppNotification, NotificationProduct } from "../types";

/**
 * The desk's notification feed: what has just happened that somebody should
 * know about, without having to be looking at the right screen.
 *
 * **An arrival changes a number and draws nothing.** There used to be a toast
 * as well: a card in the top-right corner for every notification, held on screen
 * for eight seconds and paused while hovered. It was reported as being in the
 * way, and the report was right. Somebody who has set their preferences so that
 * only what matters to them is recorded already has a signal that something is
 * waiting -- the bell's count, and the same count in the tab title -- and a card
 * landing on top of the ticket they are reading added interruption, not
 * information. So `notification.created` now lands in the feed and nowhere
 * else; the count moves, and the list behind the bell is where it is read.
 *
 * Push to the operating system is a different thing and stays. It is opt-in per
 * device, it is for when Pulse is not in front of you, and the service worker
 * already shows nothing while a Pulse window is focused -- see `public/sw.js`.
 *
 * **The list lives on the server now.** It used to be fifty items in
 * localStorage, which was fine while every notification was caused by something
 * the person was already looking at. Meeting reminders ended that: a nudge the
 * day before a meeting is written while nobody has Pulse open, and has to be
 * waiting when they next arrive -- on whichever machine they arrive at.
 *
 * Preferences moved with it. They used to be applied here, on the way in; they
 * are now applied before the row is written, so something an agent muted is
 * never recorded rather than recorded and hidden. That is also why the five
 * product events below no longer create entries: they still arrive, and they
 * still keep caches and counts honest, but what belongs in the bell is decided
 * by the server and arrives as `notification.created`.
 */

export type { AppNotification };

/**
 * Which product a notification came from, off the front of its key.
 *
 * Derived rather than carried as its own field, because the key already says
 * it and two sources of the same fact drift. Used only for the colour of a dot,
 * so an unrecognised prefix falling back to the helpdesk is a wrong shade and
 * nothing worse.
 */
export function productOf(eventKey: string): NotificationProduct {
  const prefix = eventKey.split(".")[0];
  if (prefix === "crm") return "crm";
  if (prefix === "sales") return "sales";
  if (prefix === "meeting") return "meetings";
  return "helpdesk";
}

/** Title when nothing is waiting; the unread count is prefixed to it. */
const BASE_TITLE = "Pulse";

/**
 * A local copy of the last feed, for the first paint only.
 *
 * The server is the truth; this exists because the bell's badge rendering empty
 * and then filling in is a flicker on every page load. Seeded as react-query's
 * `initialData` and overwritten by the fetch moments later.
 */
const STORAGE_KEY = "helpdesk.notifications";

interface NotificationsValue {
  items: AppNotification[];
  unreadCount: number;
  markAllRead: () => void;
  clear: () => void;
}

const NotificationsContext = createContext<NotificationsValue>({
  items: [],
  unreadCount: 0,
  markAllRead: () => {},
  clear: () => {},
});

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { socket } = useSocket();
  const queryClient = useQueryClient();

  /*
   * Shares App's cache entry, so it costs no extra request. The provider sits
   * above the router and cannot be handed the user as a prop.
   */
  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: api.me,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const feed = useQuery({
    queryKey: queryKeys.notifications,
    queryFn: api.notifications,
    // Signed out, this is a guaranteed 401.
    enabled: me.isSuccess,
    initialData: loadStored,
    /*
     * Dated to the epoch so the local copy is stale the instant it is used.
     *
     * Without this react-query treats `initialData` as fresh as of now and the
     * `staleTime` below suppresses the first fetch entirely -- the bell would
     * show whatever this browser last saw for thirty seconds, and on a machine
     * with nothing stored it would show nothing at all while the server had
     * unread rows waiting. The point is a first paint with no flicker, not a
     * cache: paint from local, refetch immediately, correct within the tick.
     */
    initialDataUpdatedAt: 0,
    /*
     * Re-read on focus. The socket delivers everything while the tab is open,
     * but a laptop that was shut for the weekend reconnects having missed the
     * reminders that fired meanwhile, and those are exactly the ones worth
     * having.
     */
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const items = feed.data?.items ?? [];

  /*
   * Tell the server what this browser's push subscription is, once we know who
   * is signed in.
   *
   * Browsers rotate subscriptions on their own account, and a rotated one is a
   * new endpoint the server has never seen -- so a device that was working
   * quietly stops, with nothing to indicate it. Re-sending whatever the browser
   * currently holds on every start is what keeps the two in step.
   *
   * It never prompts and never turns push on: with no permission or no existing
   * subscription it returns immediately. Signing in on a shared machine
   * therefore also moves the subscription to whoever is now using it, rather
   * than leaving the previous person's notifications going to it.
   */
  useEffect(() => {
    if (!me.isSuccess) return;
    void refreshPushSubscription();
  }, [me.isSuccess]);

  /* Mirrored for the next first paint. Not a cache to read from -- the query
     above owns that -- just something to draw before the fetch lands. */
  useEffect(() => {
    if (!feed.data) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(feed.data));
    } catch {
      // A full or disabled localStorage must not break notifications.
    }
  }, [feed.data]);

  /*
   * Into the cache the bell reads, and nowhere else.
   *
   * Written straight into the query rather than invalidating it: the socket
   * payload is already the row the server stored, so the badge can move the
   * moment it arrives instead of a round trip later. Focus refetches (above)
   * are what correct it if this tab missed anything while disconnected.
   */
  const push = useCallback(
    (entry: AppNotification) => {
      queryClient.setQueryData<{ items: AppNotification[]; unread: number }>(
        queryKeys.notifications,
        (current) => {
          const existing = current?.items ?? [];
          // Socket.IO can redeliver on reconnect; the row id is what makes that
          // a no-op rather than a duplicate line in the bell.
          if (existing.some((item) => item.id === entry.id)) return current;

          return {
            items: [entry, ...existing],
            unread: (current?.unread ?? 0) + (entry.read ? 0 : 1),
          };
        },
      );
    },
    [queryClient],
  );

  const markRead = useMutation({
    mutationFn: () => api.markNotificationsRead(),
    onSuccess: () =>
      queryClient.setQueryData<{ items: AppNotification[]; unread: number }>(
        queryKeys.notifications,
        (current) => ({
          items: (current?.items ?? []).map((item) => ({ ...item, read: true })),
          unread: 0,
        }),
      ),
  });

  const clearAll = useMutation({
    mutationFn: () => api.clearNotifications(),
    onSuccess: () =>
      queryClient.setQueryData(queryKeys.notifications, {
        items: [],
        unread: 0,
      }),
  });

  useEffect(() => {
    if (!socket) return;

    /* The one event that makes a notification. Everything about it -- whether
       this person wanted it, what it says, where it goes -- was decided
       server-side, so there is nothing left to judge here. */
    const onNotification = (entry: AppNotification): void => push(entry);

    /*
     * The rest keep caches honest and create nothing.
     *
     * Each is still worth handling: a mention changes the sidebar's count
     * whether or not the agent wants to be told about it, and a CRM activity
     * invalidates a timeline somebody may be looking at. Muting the bell must
     * not make those numbers wrong -- muting is about not being interrupted,
     * not about being told a different truth.
     */
    const onMention = (): void => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.viewCounts });
    };

    const onCrm = (entry: { companyId: number }): void => {
      /* Invalidated by prefix rather than exact key, because the timeline's key
         carries its filter and its branch toggle. */
      void queryClient.invalidateQueries({
        queryKey: ["crm", "timeline", entry.companyId],
      });
    };

    const onSales = (): void => {
      /* The board's counts have moved: a call assigned to you changes "assigned
         to me", and one going overdue changes the needs-attention pile. */
      void queryClient.invalidateQueries({ queryKey: ["sales"] });
    };

    socket.on("notification.created", onNotification);
    socket.on("mention.created", onMention);
    socket.on("crm.followed", onCrm);
    socket.on("sales.call", onSales);
    socket.on("sales.meeting", onSales);
    return () => {
      socket.off("notification.created", onNotification);
      socket.off("mention.created", onMention);
      socket.off("crm.followed", onCrm);
      socket.off("sales.call", onSales);
      socket.off("sales.meeting", onSales);
    };
  }, [socket, push, queryClient]);

  const unreadCount = items.filter((item) => !item.read).length;

  /*
   * The count goes in the tab title too.
   *
   * The bell's badge only works on somebody already looking at Pulse, and an
   * agent working a queue has it in a background tab most of the day -- the
   * title is the one part of the app still visible from Gmail. Same number,
   * somewhere it can be seen from outside. With no pop-up any more, this and
   * the badge are the whole of the in-app signal, which is the intent.
   */
  useEffect(() => {
    document.title = unreadCount > 0 ? `(${unreadCount}) ${BASE_TITLE}` : BASE_TITLE;
  }, [unreadCount]);

  const value = useMemo<NotificationsValue>(
    () => ({
      items,
      unreadCount,
      markAllRead: () => markRead.mutate(),
      clear: () => clearAll.mutate(),
    }),
    [items, unreadCount, markRead, clearAll],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsValue {
  return useContext(NotificationsContext);
}

/**
 * The last feed this browser saw, for the first paint.
 *
 * Anything unparseable is discarded rather than repaired. The server is about
 * to answer with the truth, so a bad local copy costs one empty render -- where
 * guessing at a half-written shape would put rows in the bell that navigate
 * nowhere.
 */
function loadStored(): { items: AppNotification[]; unread: number } {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: [], unread: 0 };

    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return { items: [], unread: 0 };

    const feed = parsed as { items?: unknown; unread?: unknown };
    if (!Array.isArray(feed.items)) return { items: [], unread: 0 };

    const items = (feed.items as AppNotification[]).filter(
      (item) => item && typeof item.id === "number" && typeof item.href === "string",
    );

    return { items, unread: typeof feed.unread === "number" ? feed.unread : 0 };
  } catch {
    return { items: [], unread: 0 };
  }
}
